import React from "react";
import { Button } from "antd";
import Icon from "@mdi/react";
import { mdiMinus, mdiPlus } from "@mdi/js";
import {
  CruiseCabinPax,
  addCabin,
  cabinPaxTotal,
  changeCabinGuest,
  removeCabin,
} from "../../../utils/cruise";
import { childAgeLabel } from "../../../utils/helpers";
import { ChildAgeRanges } from "../../../interfaces/itinerary.interface";

const Stepper = (props: {
  value: number;
  onDecrease: () => void;
  onIncrease: () => void;
  decreaseDisabled?: boolean;
  increaseDisabled?: boolean;
}) => (
  <div className="pax-row_controls">
    <Button
      shape="circle"
      className="pax-btn pax-btn--minus"
      icon={<Icon path={mdiMinus} size="18px" />}
      onClick={props.onDecrease}
      disabled={props.decreaseDisabled}
    />
    <span className="pax-count">{props.value}</span>
    <Button
      shape="circle"
      className="pax-btn pax-btn--plus"
      icon={<Icon path={mdiPlus} size="18px" />}
      onClick={props.onIncrease}
      disabled={props.increaseDisabled}
    />
  </div>
);

const CruiseTravelersV3: React.FC<{
  cabins: CruiseCabinPax[];
  setCabins: React.Dispatch<React.SetStateAction<CruiseCabinPax[]>>;
  multiplier: number;
  maxCabins: number;
  childAgeRanges?: ChildAgeRanges;
}> = ({ cabins, setCabins, multiplier, maxCabins, childAgeRanges }) => {
  const childrenAgeLabel = childAgeLabel("child_with_bed", childAgeRanges);
  const changeCabinCount = (delta: number) => {
    setCabins((prev) => (delta > 0 ? addCabin(prev, multiplier, maxCabins) : removeCabin(prev)));
  };

  const changeGuest = (index: number, key: keyof CruiseCabinPax, delta: number) => {
    setCabins((prev) => changeCabinGuest(prev, index, key, delta, multiplier));
  };

  return (
    <div className="cruise-travellers-v3">
      <div className="cruise-travellers-v3_card">
        <div className="pax-row">
          <span className="cruise-travellers-v3_cabin-count-label">Cabin</span>
          <Stepper
            value={cabins.length}
            onDecrease={() => changeCabinCount(-1)}
            onIncrease={() => changeCabinCount(1)}
            decreaseDisabled={cabins.length <= 1}
            increaseDisabled={cabins.length >= maxCabins}
          />
        </div>
      </div>

      <div className="cruise-travellers-v3_card">
        {cabins.map((cabin, index) => {
          const cabinFull = cabinPaxTotal(cabin) >= multiplier;
          return (
            <div className="cruise-travellers-v3_cabin" key={index}>
              <div className="cruise-travellers-v3_cabin-head">
                <span className="cruise-travellers-v3_cabin-title">Cabin {index + 1}</span>
                <span className="cruise-travellers-v3_cabin-max">Max. {multiplier} guests per cabin</span>
              </div>

              <div className="pax-row">
                <div className="pax-row_info">
                  <span className="pax-row_label">Adult</span>
                  <span className="pax-row_age">12 years &amp; above</span>
                </div>
                <Stepper
                  value={cabin.adult}
                  onDecrease={() => changeGuest(index, "adult", -1)}
                  onIncrease={() => changeGuest(index, "adult", 1)}
                  decreaseDisabled={cabin.adult <= 0}
                  increaseDisabled={cabinFull}
                />
              </div>

              <div className="pax-row">
                <div className="pax-row_info">
                  <span className="pax-row_label">Children</span>
                  {childrenAgeLabel && <span className="pax-row_age">{childrenAgeLabel}</span>}
                </div>
                <Stepper
                  value={cabin.children}
                  onDecrease={() => changeGuest(index, "children", -1)}
                  onIncrease={() => changeGuest(index, "children", 1)}
                  decreaseDisabled={cabin.children <= 0}
                  increaseDisabled={cabinFull}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CruiseTravelersV3;
